import reviewAPI, { FetchReviewsParams } from 'api/review'
import { API_CALL, USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE, USER_REVIEW_SET_REVIEW_STAGE } from './actionConstants'

import {
  DELETE_REVIEW,
  DELETE_REVIEW_PHOTO,
  FETCH_REVIEW_SUMMARY,
  FETCH_REVIEWS,
  FETCH_REVIEWS_ORDER,
  FETCH_USER_NUMBER_OF_REVIEWS,
  UPLOAD_REVIEW_PHOTO,
  UPSERT_REVIEW,
} from './apiActionConstants'
import { AppAction } from './ActionTypes'

type Actions = Utils.FullActionMap<{
  [USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE]: { uploadId: string, percentage: number },
  [USER_REVIEW_SET_REVIEW_STAGE]: { reviewStage: 'initial' | 'additional_fields' | 'submitted' | null },
}>

export function setReviewPhotoUploadPercentage(
  uploadId: string,
  percentage: number,
): Actions['USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE'] {
  return {
    type: USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE,
    uploadId,
    percentage,
  }
}

export function setReviewFormPage(
  reviewStage: 'initial' | 'additional_fields' | 'submitted' | null,
): Actions['USER_REVIEW_SET_REVIEW_STAGE'] {
  return {
    type: USER_REVIEW_SET_REVIEW_STAGE,
    reviewStage,
  }
}

export function fetchUserReviews(params: FetchReviewsParams) {
  return {
    type: API_CALL,
    api: FETCH_REVIEWS,
    request: () => reviewAPI.fetchReviews(params),
  }
}

export function fetchUserReviewsByOrder(orderId: string) {
  return {
    orderId,
    type: API_CALL,
    api: FETCH_REVIEWS_ORDER,
    request: () => reviewAPI.fetchReviews({ orderId }),
  }
}

export function upsertUserReview(
  params: App.CreateReviewFormParams | App.UpdateReviewParams,
  reviewStage?: 'initial' | 'additional_fields' | 'submitted' | null,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!state.auth.account.memberId) return

    const isCreate = !params.id
    const userName = params.userName || state.auth.account.fullName

    const data = isCreate ?
      {
        ...params,
        userName,
        userId: state.auth.account.memberId,
        ...(userName !== 'Anonymous' && {
          userEmail: state.auth.account.email,
          userCountry: state.auth.account.countryCode,
        }),
      } as App.CreateReviewParams :
      {
        ...params,
        userName,
        ...(userName !== 'Anonymous' && {
          userEmail: state.auth.account.email,
          userCountry: state.auth.account.countryCode,
        }),
      } as App.UpdateReviewParams

    dispatch({
      reviewStage,
      isCreate,
      type: API_CALL,
      api: UPSERT_REVIEW,
      request: () => reviewAPI.upsertReview(data),
    })
  }
}

export function removeReview(review: App.Review, orderId: string) {
  return {
    orderId,
    type: API_CALL,
    api: DELETE_REVIEW,
    request: () => reviewAPI.deleteReview(review),
  }
}

/**
 * Uploads the photo to svc-review
 */
export function uploadReviewPhoto(uploadId: string, file: File, orderId: string): AppAction {
  return (dispatch) => {
    dispatch({
      uploadId,
      type: API_CALL,
      api: UPLOAD_REVIEW_PHOTO,
      request: () => reviewAPI.uploadPhoto(
        file,
        orderId,
        (percentage) => dispatch(setReviewPhotoUploadPercentage(uploadId, percentage)),
      ),
    })
  }
}

/**
 * Deletes the photo from svc-review
 */
export function deleteReviewPhoto(imageId: string, orderId: string): AppAction {
  return {
    type: API_CALL,
    api: DELETE_REVIEW_PHOTO,
    request: () => reviewAPI.deletePhoto(imageId, orderId),
  }
}

export function fetchCustomerReviewSummary(propertyId: string, propertyProvider: string): AppAction {
  return {
    type: API_CALL,
    api: FETCH_REVIEW_SUMMARY,
    request: () => reviewAPI.fetchReviewSummary(propertyId, propertyProvider),
  }
}

export function fetchUserNumberOfReviews(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const userId = state.auth.account.memberId

    if (!userId) return

    dispatch({
      type: API_CALL,
      api: FETCH_USER_NUMBER_OF_REVIEWS,
      request: () => reviewAPI.fetchReviews({ userId }),
    })
  }
}
